import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSeoCannibalizationEditor } from '@/contexts/SeoCannibalizationEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useAuth } from '@/hooks/useAuth';
import { useGrowthSquadMembers } from '@/hooks/useGrowthSquadMembers';
import { useCreateGrowthTask } from '@/hooks/useCreateGrowthTask';
import { supabase } from '@/integrations/supabase/client';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle,
  RefreshCw,
  Sparkles,
  ArrowRight,
  CheckCircle,
  CheckCircle2,
  Eye,
  MousePointer,
  Target,
  ListPlus,
  X,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { invokeWithTimeout } from '@/utils/edgeFunctionWithTimeout';

interface CannibalizationCluster {
  id: string;
  query: string;
  cannibalization_score: number;
  primary_page: string;
  supporting_pages: Array<{
    url: string;
    clicks: number;
    impressions: number;
    position: number;
  }>;
  total_clicks: number;
  total_impressions: number;
  avg_position: number;
  keyword_difficulty: number | null;
  expected_ctr: number | null;
  traffic_gain_estimate: number | null;
  action_plan: string | null;
  created_at: string;
  task_id?: string | null;
}

export function GlobalSeoCannibalizationEditor() {
  const { isOpen, closeEditor } = useSeoCannibalizationEditor();
  const { currentBrandId, currentBrand } = useBrandManager();
  const { toast } = useToast();
  const { state: sidebarState, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { members } = useGrowthSquadMembers();
  const { createTask, isCreating } = useCreateGrowthTask();

  const [clusters, setClusters] = useState<CannibalizationCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<CannibalizationCluster | null>(null);
  const [actionPlanDialog, setActionPlanDialog] = useState(false);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [addingTask, setAddingTask] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Close drawer when route changes
  useEffect(() => {
    if (isOpen) {
      closeEditor();
    }
  }, [location.pathname]);

  // Calculate drawer dimensions
  const SIDEBAR_WIDTH = '16rem';
  const SIDEBAR_WIDTH_ICON = '3rem';
  const MOBILE_HEADER_HEIGHT = '57px';
  
  const drawerWidth = isMobile 
    ? '100%' 
    : sidebarState === 'expanded' 
      ? `calc(100% - ${SIDEBAR_WIDTH})` 
      : `calc(100% - ${SIDEBAR_WIDTH_ICON})`;
  
  const drawerLeft = isMobile 
    ? '0' 
    : sidebarState === 'expanded' 
      ? SIDEBAR_WIDTH 
      : SIDEBAR_WIDTH_ICON;

  useEffect(() => {
    if (currentBrandId && isOpen) {
      fetchClusters();
    }
  }, [currentBrandId, isOpen]);

  const fetchClusters = async () => {
    if (!currentBrandId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("seo_gsc_cannibalization_clusters")
        .select("*")
        .eq("brand_id", currentBrandId)
        .order("cannibalization_score", { ascending: false });

      if (error) throw error;

      const parsedData = (data || []).map((cluster: any) => ({
        ...cluster,
        supporting_pages: Array.isArray(cluster.supporting_pages)
          ? cluster.supporting_pages
          : [],
      }));

      setClusters(parsedData);
    } catch (error) {
      console.error("Error fetching clusters:", error);
      toast({
        title: "Error",
        description: "Failed to fetch cannibalization data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDetectCannibalization = async () => {
    if (!currentBrandId) return;

    try {
      setDetecting(true);
      toast({
        title: "Detecting Cannibalization",
        description: "Analyzing queries for multi-page ranking...",
      });

      const { data, error } = await invokeWithTimeout(
        supabase,
        "detect-cannibalization",
        {
          body: { brand_id: currentBrandId, minImpressions: 50 },
          timeout: 120000,
        }
      );

      if (error) throw error;

      toast({
        title: "Success",
        description: `Found ${data?.count || 0} cannibalization issues`,
      });

      fetchClusters();
    } catch (error: any) {
      console.error("Error detecting cannibalization:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to detect cannibalization",
        variant: "destructive",
      });
    } finally {
      setDetecting(false);
    }
  };

  const handleGenerateActionPlan = async (cluster: CannibalizationCluster) => {
    if (!currentBrandId) return;

    try {
      setGeneratingPlan(true);
      setSelectedCluster(cluster);

      toast({
        title: "Generating Action Plan",
        description: "AI is creating your consolidation strategy...",
      });

      const primaryPageData = {
        position: cluster.avg_position,
        clicks: cluster.total_clicks - cluster.supporting_pages.reduce((sum, p) => sum + p.clicks, 0),
        impressions:
          cluster.total_impressions -
          cluster.supporting_pages.reduce((sum, p) => sum + p.impressions, 0),
      };

      const { data, error } = await invokeWithTimeout(
        supabase,
        "gemini-cannibalization",
        {
          body: {
            brand_id: currentBrandId,
            query: cluster.query,
            primaryPage: cluster.primary_page,
            supportingPages: cluster.supporting_pages,
            metrics: {
              primaryPosition: primaryPageData.position,
              primaryClicks: primaryPageData.clicks,
              primaryImpressions: primaryPageData.impressions,
              totalClicks: cluster.total_clicks,
              totalImpressions: cluster.total_impressions,
              avgPosition: cluster.avg_position,
              cannibalizationScore: cluster.cannibalization_score,
              targetPosition: cluster.avg_position * 0.6,
              trafficGain: cluster.traffic_gain_estimate,
            },
          },
          timeout: 120000,
        }
      );

      if (error) throw error;

      const { error: updateError } = await supabase
        .from("seo_gsc_cannibalization_clusters")
        .update({ action_plan: data?.actionPlan })
        .eq("id", cluster.id);

      if (updateError) throw updateError;

      setClusters((prev) =>
        prev.map((c) => (c.id === cluster.id ? { ...c, action_plan: data?.actionPlan } : c))
      );

      setSelectedCluster((prev) =>
        prev ? { ...prev, action_plan: data?.actionPlan } : null
      );

      setActionPlanDialog(true);

      toast({
        title: "Success",
        description: "Action plan generated successfully",
      });
    } catch (error: any) {
      console.error("Error generating action plan:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to generate action plan",
        variant: "destructive",
      });
    } finally {
      setGeneratingPlan(false);
    }
  };

  const toggleClusterExpand = (clusterId: string) => {
    const newExpanded = new Set(expandedClusters);
    if (newExpanded.has(clusterId)) {
      newExpanded.delete(clusterId);
    } else {
      newExpanded.add(clusterId);
    }
    setExpandedClusters(newExpanded);
  };

  const handleAddToTask = async (cluster: CannibalizationCluster) => {
    if (!currentBrandId) return;

    const currentMember = members.find(m => m.user_id === user?.id);
    if (!currentMember) {
      toast({ 
        title: 'Error', 
        description: 'You must be a squad member to create tasks', 
        variant: 'destructive' 
      });
      return;
    }

    try {
      setAddingTask(true);

      const supportingPagesText = cluster.supporting_pages
        .map((p) => `- ${p.url} (Pos: ${p.position.toFixed(1)}, Clicks: ${p.clicks})`)
        .join('\n');

      const description = `**Query:** ${cluster.query}

**Primary Page (Keep):** ${cluster.primary_page}

**Supporting Pages (Redirect with 301):**
${supportingPagesText}

**Recommendation:** ${cluster.action_plan 
  ? cluster.action_plan.substring(0, 500) 
  : `Consolidate content from supporting pages into primary page with 301 redirects`}

**Metrics:**
- Cannibalization Score: ${cluster.cannibalization_score.toFixed(1)}
- Total Clicks: ${cluster.total_clicks}
- Total Impressions: ${cluster.total_impressions}
- Avg Position: ${cluster.avg_position.toFixed(1)}
- Potential Click Gain: ${cluster.traffic_gain_estimate || 0}`;

      const taskData = await createTask({
        title: `Fix Cannibalization: "${cluster.query}"`,
        description,
        priority: cluster.cannibalization_score >= 10 ? 'High' : cluster.cannibalization_score >= 5 ? 'Medium' : 'Low',
        difficulty: 'Medium',
        assigned_to: currentMember.id,
        brand_id: currentBrandId,
        source_type: 'seo_cannibalization',
        source_data: {
          cluster_id: cluster.id,
          query: cluster.query,
          primary_page: cluster.primary_page,
          supporting_pages: cluster.supporting_pages
        }
      });

      if (taskData?.id) {
        await supabase
          .from('seo_gsc_cannibalization_clusters')
          .update({ task_id: taskData.id })
          .eq('id', cluster.id);

        fetchClusters();
      }

      toast({
        title: "Task Created",
        description: "Cannibalization fix added to foundational tasks",
      });
    } catch (error) {
      console.error("Error adding task:", error);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      });
    } finally {
      setAddingTask(false);
    }
  };

  const handleViewTask = async (taskId: string) => {
    const { data: task } = await supabase
      .from('foundational_tasks')
      .select('slug')
      .eq('id', taskId)
      .single();
    
    if (task?.slug && currentBrandId) {
      const { data: brand } = await supabase
        .from('brands')
        .select('slug')
        .eq('id', currentBrandId)
        .single();
      
      if (brand?.slug) {
        navigate(`/task/${brand.slug}/${task.slug}`);
        return;
      }
    }
    navigate(`/tasks?task=${taskId}`);
  };

  const getSeverityColor = (score: number): string => {
    if (score >= 10) return "text-red-600";
    if (score >= 5) return "text-amber-600";
    return "text-yellow-600";
  };

  const getSeverityLabel = (score: number): string => {
    if (score >= 10) return "Severe";
    if (score >= 5) return "Moderate";
    return "Mild";
  };

  const getSeverityBadge = (score: number): "destructive" | "secondary" | "outline" => {
    if (score >= 10) return "destructive";
    if (score >= 5) return "secondary";
    return "outline";
  };

  const stats = useMemo(() => {
    return {
      totalIssues: clusters.length,
      severeIssues: clusters.filter((c) => c.cannibalization_score >= 10).length,
      totalTrafficAtRisk: clusters.reduce((sum, c) => sum + c.total_clicks, 0),
      potentialGain: clusters.reduce((sum, c) => sum + (c.traffic_gain_estimate || 0), 0),
    };
  }, [clusters]);

  const paginatedClusters = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return clusters.slice(startIndex, endIndex);
  }, [clusters, currentPage, itemsPerPage]);

  return (
    <>
      <Drawer 
        open={isOpen} 
        onOpenChange={(open) => !open && closeEditor()}
        direction="right"
        modal={false}
      >
        <DrawerContent
          className="fixed inset-y-0 right-0 h-full border-l bg-background shadow-lg focus:outline-none mt-0"
          style={{ 
            width: drawerWidth,
            left: drawerLeft,
            top: isMobile ? MOBILE_HEADER_HEIGHT : 0,
            height: isMobile ? `calc(100% - ${MOBILE_HEADER_HEIGHT})` : '100%',
          }}
        >
          {/* Fixed close button */}
          <div className="absolute top-2 right-2 z-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={closeEditor}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ScrollArea className="h-full">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Header */}
              <div className="flex flex-col gap-2 pr-10">
                {/* Row 1: Title */}
                <h2 className="text-xl sm:text-2xl font-bold">Cannibalization Detection</h2>
                {/* Row 2: Subtitle */}
                <p className="text-sm text-muted-foreground">
                  Identify and resolve queries ranking on multiple pages
                </p>
                {/* Row 3: Button (right-aligned on mobile, inline on desktop) */}
                <div className="flex justify-end sm:hidden">
                  <Button 
                    onClick={handleDetectCannibalization} 
                    disabled={detecting}
                    size="sm"
                  >
                    {detecting ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Detecting...
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Detect
                      </>
                    )}
                  </Button>
                </div>
                {/* Desktop: Button inline with title */}
                <div className="hidden sm:flex sm:justify-end sm:-mt-12">
                  <Button 
                    onClick={handleDetectCannibalization} 
                    disabled={detecting}
                  >
                    {detecting ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Detecting...
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Detect Issues
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardDescription className="text-xs sm:text-sm">Total Issues</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                      <p className="text-lg sm:text-2xl font-bold">{stats.totalIssues}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardDescription className="text-xs sm:text-sm">Severe Issues</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 hidden sm:block" />
                      <p className="text-lg sm:text-2xl font-bold text-red-600">{stats.severeIssues}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardDescription className="text-xs sm:text-sm">Traffic at Risk</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="flex items-center gap-2">
                      <MousePointer className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                      <p className="text-lg sm:text-2xl font-bold">{stats.totalTrafficAtRisk.toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardDescription className="text-xs sm:text-sm">Potential Gain</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="flex items-center gap-2">
                      <Target className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 hidden sm:block" />
                      <p className="text-lg sm:text-2xl font-bold text-green-600">
                        +{stats.potentialGain.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Content */}
              {loading ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : clusters.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
                    <CheckCircle2 className="h-12 w-12 sm:h-16 sm:w-16 text-green-600 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold mb-2">No Cannibalization Detected</h3>
                    <p className="text-sm text-muted-foreground text-center">
                      Great! No queries are ranking on multiple pages.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="sm:hidden space-y-3">
                    {paginatedClusters.map((cluster) => (
                      <Card key={cluster.id} className="overflow-hidden">
                        <Collapsible
                          open={expandedClusters.has(cluster.id)}
                          onOpenChange={() => toggleClusterExpand(cluster.id)}
                        >
                          <CollapsibleTrigger asChild>
                            <CardContent className="p-3 cursor-pointer">
                              <div className="space-y-2">
                                <p className="font-medium text-sm line-clamp-2">{cluster.query}</p>
                                <div className="grid grid-cols-4 gap-2 text-xs">
                                  <div>
                                    <p className="text-muted-foreground">Score</p>
                                    <p className={`font-bold ${getSeverityColor(cluster.cannibalization_score)}`}>
                                      {cluster.cannibalization_score.toFixed(1)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Pages</p>
                                    <p className="font-medium">{cluster.supporting_pages.length + 1}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Clicks</p>
                                    <p className="font-medium">{cluster.total_clicks}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Gain</p>
                                    <p className="font-medium text-green-600">+{cluster.traffic_gain_estimate || 0}</p>
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-2">
                                  <Button
                                    size="sm"
                                    variant={cluster.action_plan ? "outline" : "default"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (cluster.action_plan) {
                                        setSelectedCluster(cluster);
                                        setActionPlanDialog(true);
                                      } else {
                                        handleGenerateActionPlan(cluster);
                                      }
                                    }}
                                    disabled={generatingPlan}
                                  >
                                    {generatingPlan && selectedCluster?.id === cluster.id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : cluster.action_plan ? (
                                      <>
                                        <Eye className="h-4 w-4 mr-1" />
                                        View
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="h-4 w-4 mr-1" />
                                        Plan
                                      </>
                                    )}
                                  </Button>
                                  {cluster.task_id ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewTask(cluster.task_id!);
                                      }}
                                      className="text-green-600 border-green-600"
                                    >
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Tasked
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddToTask(cluster);
                                      }}
                                      disabled={addingTask || isCreating}
                                    >
                                      <ListPlus className="h-4 w-4 mr-1" />
                                      Task
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-3 pb-3 space-y-3 border-t pt-3">
                              <div>
                                <h4 className="text-xs font-semibold mb-1 flex items-center gap-1 text-green-600">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Primary Page
                                </h4>
                                <p className="text-xs font-mono bg-green-50 dark:bg-green-950/20 p-2 rounded truncate">
                                  {cluster.primary_page}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-xs font-semibold mb-1 flex items-center gap-1 text-amber-600">
                                  <ArrowRight className="h-3 w-3" />
                                  Redirect Pages
                                </h4>
                                <div className="space-y-1">
                                  {cluster.supporting_pages.map((page, idx) => (
                                    <p key={idx} className="text-xs font-mono bg-amber-50 dark:bg-amber-950/20 p-2 rounded truncate">
                                      {page.url}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <Card className="hidden sm:block">
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[300px]">Query</TableHead>
                              <TableHead className="text-center">Severity</TableHead>
                              <TableHead className="text-center">Pages</TableHead>
                              <TableHead className="text-right">Position</TableHead>
                              <TableHead className="text-right">Clicks</TableHead>
                              <TableHead className="text-right">Traffic Gain</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paginatedClusters.map((cluster) => (
                              <Collapsible
                                key={cluster.id}
                                open={expandedClusters.has(cluster.id)}
                                onOpenChange={() => toggleClusterExpand(cluster.id)}
                                asChild
                              >
                                <>
                                  <TableRow className="cursor-pointer hover:bg-muted/50">
                                    <CollapsibleTrigger asChild>
                                      <TableCell className="font-medium">
                                        <span className="truncate max-w-[250px] block">{cluster.query}</span>
                                      </TableCell>
                                    </CollapsibleTrigger>
                                    <TableCell className="text-center">
                                      <div className="flex flex-col items-center gap-1">
                                        <span
                                          className={`text-xl font-bold ${getSeverityColor(
                                            cluster.cannibalization_score
                                          )}`}
                                        >
                                          {cluster.cannibalization_score.toFixed(1)}
                                        </span>
                                        <Badge variant={getSeverityBadge(cluster.cannibalization_score)}>
                                          {getSeverityLabel(cluster.cannibalization_score)}
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge variant="outline">{cluster.supporting_pages.length + 1}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right">{cluster.avg_position.toFixed(1)}</TableCell>
                                    <TableCell className="text-right">{cluster.total_clicks.toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-green-600 font-semibold">
                                      +{cluster.traffic_gain_estimate || 0}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <Button
                                          size="sm"
                                          variant={cluster.action_plan ? "outline" : "default"}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (cluster.action_plan) {
                                              setSelectedCluster(cluster);
                                              setActionPlanDialog(true);
                                            } else {
                                              handleGenerateActionPlan(cluster);
                                            }
                                          }}
                                          disabled={generatingPlan}
                                        >
                                          {generatingPlan && selectedCluster?.id === cluster.id ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                          ) : cluster.action_plan ? (
                                            <>
                                              <Eye className="h-4 w-4 mr-1" />
                                              View
                                            </>
                                          ) : (
                                            <>
                                              <Sparkles className="h-4 w-4 mr-1" />
                                              Plan
                                            </>
                                          )}
                                        </Button>
                                        {cluster.task_id ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewTask(cluster.task_id!);
                                            }}
                                            className="text-green-600 border-green-600"
                                          >
                                            <CheckCircle className="h-4 w-4 mr-1" />
                                            Tasked
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAddToTask(cluster);
                                            }}
                                            disabled={addingTask || isCreating}
                                          >
                                            <ListPlus className="h-4 w-4 mr-1" />
                                            Task
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                  <CollapsibleContent asChild>
                                    <TableRow>
                                      <TableCell colSpan={7} className="bg-muted/30 p-6">
                                        <div className="space-y-4">
                                          <div>
                                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                                              Primary Page (Keep & Strengthen):
                                            </h4>
                                            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded p-3">
                                              <p className="text-sm font-mono truncate">{cluster.primary_page}</p>
                                            </div>
                                          </div>

                                          <div>
                                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                              <ArrowRight className="h-4 w-4 text-amber-600" />
                                              Supporting Pages (Redirect with 301):
                                            </h4>
                                            <div className="space-y-2">
                                              {cluster.supporting_pages.map((page, idx) => (
                                                <div
                                                  key={idx}
                                                  className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded p-3"
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <p className="text-sm font-mono truncate max-w-[400px]">
                                                      {page.url}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                      <span>Pos: {page.position.toFixed(1)}</span>
                                                      <span>Clicks: {page.clicks}</span>
                                                      <span>Impr: {page.impressions.toLocaleString()}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  </CollapsibleContent>
                                </>
                              </Collapsible>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Pagination */}
                      {clusters.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-4 border-t gap-3">
                          <div className="flex items-center gap-2 sm:gap-4">
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, clusters.length)} of {clusters.length}
                            </p>
                            <Select
                              value={itemsPerPage.toString()}
                              onValueChange={(value) => {
                                setItemsPerPage(Number(value));
                                setCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-[80px] sm:w-[100px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </Button>
                            <span className="text-xs sm:text-sm">
                              {currentPage} / {Math.ceil(clusters.length / itemsPerPage)}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(Math.min(Math.ceil(clusters.length / itemsPerPage), currentPage + 1))}
                              disabled={currentPage >= Math.ceil(clusters.length / itemsPerPage)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Mobile Pagination */}
                  <div className="sm:hidden flex items-center justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-xs">
                      {currentPage} / {Math.ceil(clusters.length / itemsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(Math.ceil(clusters.length / itemsPerPage), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(clusters.length / itemsPerPage)}
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* Action Plan Dialog */}
      <Dialog open={actionPlanDialog} onOpenChange={setActionPlanDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI-Generated Consolidation Action Plan
            </DialogTitle>
            <DialogDescription>
              Query: <span className="font-semibold">{selectedCluster?.query}</span>
            </DialogDescription>
          </DialogHeader>
          {selectedCluster?.action_plan && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap">{selectedCluster.action_plan}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
